import { useState } from "react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { CalendarIcon, X } from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";

const presets = [
  { label: "Today", getValue: () => ({ from: new Date(), to: new Date() }) },
  { label: "Last 7 days", getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }) },
  { label: "Last 30 days", getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
  { label: "This month", getValue: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
  { label: "Last month", getValue: () => ({ from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) }) },
];

const DateRangePicker = ({ dateRange, onDateRangeChange, className = "" }) => {
  const [open, setOpen] = useState(false);

  const handlePreset = (preset) => {
    const range = preset.getValue();
    onDateRangeChange(range);
    setOpen(false);
  };

  const handleClear = () => {
    onDateRangeChange({ from: null, to: null });
    setOpen(false);
  };

  const formatDateRange = () => {
    if (!dateRange?.from) return "Select date range";
    if (!dateRange?.to || dateRange.from.toDateString() === dateRange.to.toDateString()) {
      return format(dateRange.from, "MMM d, yyyy");
    }
    return `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d, yyyy")}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`justify-start text-left font-normal bg-white border-[--border-dark] hover:bg-[--accent-pale] hover:border-[--accent-soft] rounded-lg text-[13px] ${
            !dateRange?.from ? "text-[--text-muted]" : "text-[--text-primary]"
          } ${className}`}
          data-testid="date-range-picker"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDateRange()}
          {dateRange?.from && (
            <X
              className="ml-2 h-4 w-4 hover:text-[#BC4B35]"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start">
        <div className="flex">
          {/* Presets */}
          <div className="border-r border-[#E7E5E4] p-2 space-y-1">
            {presets.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handlePreset(preset)}
                className="w-full text-left px-3 py-2 text-sm rounded hover:bg-[#F5F2EA] text-[#57534E]"
              >
                {preset.label}
              </button>
            ))}
            <hr className="my-2 border-[#E7E5E4]" />
            <button
              onClick={handleClear}
              className="w-full text-left px-3 py-2 text-sm rounded hover:bg-[#F5F2EA] text-[#BC4B35]"
            >
              Clear
            </button>
          </div>
          
          {/* Calendar */}
          <div className="p-3">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={(range) => {
                onDateRangeChange(range);
                if (range?.from && range?.to) {
                  setOpen(false);
                }
              }}
              numberOfMonths={2}
              initialFocus
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
